import { Fragment, useMemo, useState, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import {
  faCamera,
  faExclamationCircle,
  faSyncAlt,
  faHeartbeat
} from "@fortawesome/free-solid-svg-icons";
import Pills from "../pills";
import Container from "../container";
import idGen from "../../utils/idGen";
import { fetchPage } from "../../utils/fetch";
import { explorer } from "../../transport";
import { useMainContext } from "../../hooks/useMainContext";
import cloneDeep from "lodash.clonedeep";
import moment from "moment";

import RequestBox from "../requestBox";

import styles from "./styles.module.scss";

export default function Snapshots(props) {
  const { itemId, endpointId, id_token, tenant } = props;
  const { requestPromiseDeleteConfirm } = useMainContext();
  const queryClient = useQueryClient();
  const snapId = useMemo(() => idGen(), []);
  const emptySnap = useMemo(
    () => ({ id: snapId, title: "New Snapshot", isEmpty: true }),
    [snapId]
  );
  const [currentTab, setCurrentTab] = useState(snapId);
  const [deletingSnapIndex, setDeletingSnapIndex] = useState(-1);
  const [snapshots, setSnapshots] = useState({
    snapshots: [emptySnap]
  });

  const loadSnapshot = useCallback(
    ({ fromCache = false } = {}) => {
      const url = `snapshot/${tenant}/${endpointId}:${itemId}`;
      const show = (data) => {
        if (data?.snapshots?.length > 0) {
          setCurrentTab(data.snapshots[0].id);
          setSnapshots(data);
        }
      };

      // On mount, show the snapshots received last time (e.g. switching back
      // to an already seen variant) while they are requested again; never after
      // a save/delete, which already updated the state. Cloned in and out of
      // the cache because the snapshots state is updated in place.
      const cached = fromCache && queryClient.getQueryData(["snapshots", url]);
      if (cached) show(structuredClone(cached));

      explorer
        .get(url, {
          headers: { "id-token": id_token }
        })
        .then((response) => {
          queryClient.setQueryData(
            ["snapshots", url],
            structuredClone(response.data)
          );
          show(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    },
    [endpointId, id_token, itemId, queryClient, tenant]
  );

  const saveData = useCallback(
    (data, cb, cbError) => {
      data.id = `${endpointId}:${itemId}`;

      explorer
        .post(`snapshot/${tenant}/${endpointId}:${itemId}`, data, {
          headers: { "id-token": id_token }
        })
        .then((response) => {
          loadSnapshot();
          if (cb) cb(response);
        })
        .catch((error) => {
          if (cbError) cbError(error);
        });
    },
    [endpointId, id_token, itemId, loadSnapshot, tenant]
  );

  const saveSnapshot = useCallback(
    (snapIndex, snap, cb, cbError) => {
      const newData = { ...snapshots };
      newData.snapshots[snapIndex] = snap;

      saveData(newData, cb, cbError);
    },
    [saveData, snapshots]
  );

  const removeSnap = useCallback(
    (snaps, index) => {
      setDeletingSnapIndex(index);
      const newSnaps = { ...snapshots };
      newSnaps.snapshots = snaps;

      explorer
        .post(`snapshot/${tenant}/${endpointId}:${itemId}`, newSnaps, {
          headers: { "id-token": id_token }
        })
        .then((response) => {
          if (newSnaps.snapshots.length === 0) {
            newSnaps.snapshots.push(emptySnap);
          }

          loadSnapshot();
          setDeletingSnapIndex(-1);
        })
        .catch((error) => {
          console.log(error);
          setDeletingSnapIndex(-1);
        });
    },
    [emptySnap, endpointId, id_token, itemId, loadSnapshot, snapshots, tenant]
  );

  const changeSnapTitle = useCallback(
    (index, title) => {
      const newSnaps = structuredClone(snapshots);
      newSnaps.snapshots[index].title = title;
      setSnapshots(newSnaps);
    },
    [snapshots]
  );

  const snapPills = useMemo(() => {
    const pills = [];

    snapshots.snapshots.forEach((snap, snapIndex) => {
      const [icon, iconStatus] = (() => {
        if (deletingSnapIndex === snapIndex) return [faSyncAlt, ""];
        if (!snap.paramsId) return [faExclamationCircle, styles.snapNoActive];
        if (snap.status === "OK") return [faCamera, styles.snapOK];
        if (snap.status === "KO") return [faCamera, styles.snapKO];

        return [faCamera, ""];
      })();

      pills.push({
        id: snap.id,
        tab: {
          title: snap.title,
          icon: (
            <FontAwesomeIcon
              icon={icon}
              className={`${styles.tabIcon} ${iconStatus}`}
              spin={deletingSnapIndex === snapIndex}
            />
          ),
          editable: true,
          onEdit: (snaps) => {
            const newSnaps = structuredClone(snapshots);
            newSnaps.snapshots = snaps;
            setSnapshots(newSnaps);
          },
          deletable: true,
          onConfirmDelete: () =>
            requestPromiseDeleteConfirm(
              `Are you sure to delete ${snap.title} snapshot?`
            ),
          onDelete: removeSnap,
          draggable: true,
          onDrag: (snaps) => {
            const newSnaps = structuredClone(snapshots);
            newSnaps.snapshots = snaps;
            setSnapshots(newSnaps);
          }
        },
        pane: (
          <Snapshot
            key={snap.id}
            data={snap}
            snapIndex={snapIndex}
            changeSnapTitle={changeSnapTitle}
            saveSnapshot={saveSnapshot}
            {...props}
          />
        )
      });
    });

    return pills;
  }, [
    changeSnapTitle,
    deletingSnapIndex,
    props,
    removeSnap,
    requestPromiseDeleteConfirm,
    saveSnapshot,
    snapshots
  ]);

  useEffect(() => {
    if (endpointId && itemId) {
      loadSnapshot({ fromCache: true });
    }
  }, [endpointId, itemId, loadSnapshot]);

  const title = useMemo(() => {
    return (
      <Fragment>
        <FontAwesomeIcon icon={faHeartbeat} className={styles.healthIcon} />
        Snapshots
      </Fragment>
    );
  }, []);

  const header = useMemo(() => {
    let status = "NOT VERIFIED";

    for (const s of snapshots?.snapshots ?? []) {
      if (s.status !== "OK") {
        status = "KO";
        break;
      }
      status = "OK";
    }

    return (
      <div className={styles.compact}>
        <span className={styles.label}>Snaps</span>
        <span className={styles.value}>
          {snapshots?.snapshots?.length > 0 &&
          snapshots.snapshots[0].isEmpty !== true ? (
            <span>{snapshots.snapshots.length}</span>
          ) : (
            <span className={styles.tbd}>No Snapshots Taken yet</span>
          )}
        </span>

        <span className={styles.label}>Status</span>
        <span className={styles.value}>{status}</span>
        <span className={styles.label}>#id</span>
        <span className={styles.value}>{`${endpointId}:${
          itemId || "TBD"
        }`}</span>
      </div>
    );
  }, [endpointId, itemId, snapshots.snapshots]);

  return (
    <Container
      title={title}
      help="snapshots"
      color="#240000"
      header={header}
      folded={true}
    >
      <div className={styles.extended}>
        <Pills
          id="snapshot-pills"
          enableReinitialize={true}
          originData={snapshots.snapshots}
          allowEmpty={true}
          tabs={snapPills}
          onChange={(_, id) => setCurrentTab(id)}
          currentTabId={currentTab}
          addTab={{
            enabled: true,
            getNewData: () => ({
              id: idGen(),
              title: `New Snapshot #${snapshots.snapshots.length + 1}`,
              isEmpty: true
            }),
            onAdd: (snaps) => {
              const newSnaps = structuredClone(snaps);
              newSnaps.snapshots = snaps;
              setSnapshots(newSnaps);
              setCurrentTab(snaps.at(-1).id);
            }
          }}
        />
      </div>
    </Container>
  );
}

function Snapshot(props) {
  const {
    itemId,
    propertyId,
    propertyRef,
    endpointId,
    endpointRef,
    id_token,
    tenant,
    user,
    snapIndex,
    saveSnapshot,
    changeSnapTitle
  } = props;
  const [data, setData] = useState(props.data);
  const [snapState, setSnapState] = useState("still");
  const [description, setDescription] = useState(props.data?.description);
  const [uiSets] = useState(data.uiSets);

  const canSnap = useMemo(() => {
    const paramsId = data?.paramsId;
    const userId = data?.userId;
    const contentId = data?.contentId;

    if (!paramsId || (!userId && !contentId)) return false;

    return true;
  }, [data]);

  const variant = useMemo(() => {
    if (snapState === "still") return "outline-primary";
    if (snapState === "progress") return "outline-warning";
    if (snapState === "done") return "outline-success";

    return "outline-danger";
  }, [snapState]);

  const label = useMemo(() => {
    if (snapState === "still") return "Create Snapshot";
    if (snapState === "progress") return "Creating...";
    if (snapState === "done") return "Snapshot Created!";
    return `Error:  ${snapState}`;
  }, [snapState]);

  const handleChangeTitle = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const value = e.target.value;
      //setTitle(value);
      changeSnapTitle(snapIndex, value);
    },
    [changeSnapTitle, snapIndex]
  );

  const snap = useCallback(() => {
    if (!canSnap) return;

    const params = cloneDeep(data);
    delete params.snapshot;
    delete params.payload;

    const body = {
      ...params,
      tenant,
      blockId: itemId,
      endpointId: endpointRef,
      propertyId: propertyRef,
      avoidDecorators: true
    };

    setSnapState("progress");
    fetchPage(
      body,
      id_token,
      (snapshot, payload) => {
        delete payload.query?.id;
        delete payload.query?.title;

        const snap = {
          ...params,
          id: data.id,
          title: props.data.title,
          description: description,
          isEmpty: false,
          taken: new Date(),
          shooter: user,
          itemId,
          propertyId,
          propertyRef,
          endpointId,
          endpointRef,
          uiSets,
          snapIndex,
          payload,
          snapshot
        };

        saveSnapshot(
          snapIndex,
          snap,
          () => {
            setSnapState("done");
            setTimeout(() => setSnapState("still"), 2000);
          },
          (error) => {
            setSnapState(error);
            setTimeout(() => setSnapState("still"), 5000);
          }
        );
      },
      (error) => {
        console.log(error);
        setSnapState(error);
        setTimeout(() => setSnapState("still"), 5000);
      }
    );
  }, [
    canSnap,
    data,
    description,
    endpointId,
    endpointRef,
    id_token,
    itemId,
    propertyId,
    propertyRef,
    props.data.title,
    saveSnapshot,
    snapIndex,
    tenant,
    uiSets,
    user
  ]);

  return (
    <div className={styles.snapBox}>
      {data.taken ? (
        <div className={styles.row}>
          <span className={styles.label}>Shooter</span>
          <span className={styles.value}>{data.shooter}</span>
          <span className={styles.label}>Taken</span>
          <span className={styles.value}>
            {moment(data.taken).format("dddd, MMMM Do YYYY, h:mm:ss a")}
          </span>
          <span className={styles.label}>Status</span>
          <span className={styles.value}>
            {data.status ? data.status : "Not Verified"}
          </span>
        </div>
      ) : null}
      <div className={styles.row}>
        <span className={styles.label}>Title</span>
        <Form.Control
          type="text"
          value={props.data?.title || ""}
          onChange={handleChangeTitle}
        />
      </div>
      <div className={styles.row}>
        <span className={styles.label}>Description</span>
        <Form.Control
          as="textarea"
          rows="3"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <RequestBox
        parameters={data.uiParams}
        onChange={({ uiParams, currentParams }) =>
          setData({ ...data, ...currentParams, uiParams })
        }
        allowCollection={false}
        forceOpen={false}
      />

      {canSnap && (
        <div className={styles.footer}>
          <Button
            size="sm"
            className={styles.snapBtn}
            onClick={snap}
            variant={variant}
          >
            <FontAwesomeIcon icon={faCamera} className={styles.icon} />
            <span className={styles.label}>{label}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
